import { ApiError } from '../services/api/_client'

/**
 * 例外が持っている意味をそのまま画面に出すための対応表 (#1624 の議論)。
 *
 * **なぜ一律の「読み込めませんでした」ではだめか**: 401 と 403 と 500 は
 * ユーザーが取るべき行動が違う (ログインし直す / 諦める / 待つ)。一律にすると
 * どれも「再読み込み」に見えるが、権限が無い相手に再読み込みを勧めても永久に
 * 直らないし、index 漏れのような決定論的な失敗も同じく直らない。
 *
 * ApiError 側は status ごとのメッセージを既に持っているが、**日本語のベタ書き**で
 * 顧客画面は 10 ロケール必須。そこで status → i18n キーに対応付けて、意味は
 * 保ったまま各言語で出す。
 *
 * **「0 件」とは別物**であることに注意。取得できて 0 件なのか、そもそも
 * 取得できなかったのかは意味が違うので、呼び出し側で状態を分けて持つこと。
 */
/**
 * 決済系 API が `{code, message}` で返す「断った理由」→ 客に見せる文言のキー (#1801)。
 *
 * backend は境界で理由 (現金のみ運用 / ポイント不可 / 上限) を返すが、画面が
 * `checkout.failed` の一言に畳んでいて「レジで払ってください」が伝わらなかった。
 * QRScan にだけあった対応表をここに集約する。該当しない code は null (呼び出し側の fallback)。
 */
const PAYMENT_DECLINE_KEYS: Record<string, string> = {
  PAYMENT_LIMIT_TX: 'payment.limitPerTx',
  PAYMENT_LIMIT_USER_DAILY: 'payment.limitUserDaily',
  PAYMENT_LIMIT_USER_RATE: 'payment.limitUserRate',
  PAYMENT_LIMIT_CARD_DAILY: 'payment.limitCardDaily',
  PAYMENT_LIMIT_CARD_RATE: 'payment.limitCardTooFast',
  // #1801 現金のみ運用の店 (アプリ内カード不可)
  cash_only_store: 'payment.cashOnlyStore',
  // #1438 / #1769 ポイントを受けられない店 (断っている / KYC 未完了)
  points_not_accepted: 'scan.pointsNotAccepted',
  points_require_kyc: 'scan.pointsNotAccepted',
  // #2039 時間料金はアプリ内決済に載せない (決済の途中で金額が動くのを避ける)。レジへ案内する
  time_charge_register_only: 'payment.timeChargeAtRegister',
  // #2155 見せた額と払う瞬間の額が違う (同席者が先に払った・お通しが外された等)。読み直す
  amount_changed: 'payment.amountChanged',
  // #2190 同じお会計の前の決済が成立していた (確定が届かずに閉じた等)。新しい決済は作られていない
  previous_payment_settled: 'checkout.previousSettled',
  // #2190 前の決済がまだ処理中。払い直すと二重になりうるので待ってもらう
  previous_payment_processing: 'checkout.previousProcessing',
  // #2190 前の決済が成立したか確かめられなかった。少し待ってからやり直してもらう
  previous_payment_unverified: 'checkout.previousUnverified',
  // #2190 テイクアウト: 前のお支払いで注文を受け付けていた (画面は専用の段で番号と額を出す)
  previous_order_placed: 'takeout.previousOrderTitle',
}

export function paymentDeclineI18nKey(err: unknown): string | null {
  if (err instanceof ApiError && err.code && PAYMENT_DECLINE_KEYS[err.code]) {
    return PAYMENT_DECLINE_KEYS[err.code]
  }
  return null
}

export function isCashOnlyStoreError(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'cash_only_store'
}

export function apiErrorI18nKey(err: unknown): string {
  if (!(err instanceof ApiError)) {
    // fetch 自体が失敗した場合 (通信断 / DNS / CORS)。ApiError にならない
    return 'common.apiError.network'
  }
  if (err.status === 401) return 'common.apiError.unauthorized'
  if (err.status === 403) return 'common.apiError.forbidden'
  if (err.status === 404) return 'common.apiError.notFound'
  if (err.status === 422) return 'common.apiError.invalid'
  if (err.status === 429) return 'common.apiError.rateLimited'
  if (err.status >= 500) return 'common.apiError.server'
  return 'common.apiError.server'
}

/**
 * 画面に併記する識別子。`500` や `500 / FAILED_PRECONDITION` の形。
 *
 * 文言はロケールごとに変わるので、それだけでは問い合わせを受けても何が
 * 起きたか特定できない。**status と code を添える**と、ユーザーがスクショを
 * 送ってきた時点で原因の当たりがつく (index 漏れなら 500、権限なら 403)。
 * 生の例外メッセージを出さないのは、日本語ベタ書き + backend の英語 detail が
 * そのまま多言語 UI に出てしまうため。
 */
export function apiErrorRef(err: unknown): string {
  if (!(err instanceof ApiError)) return 'network'
  return err.code ? `${err.status} / ${err.code}` : String(err.status)
}

/**
 * 再読み込みで直る見込みがあるか。
 *
 * 4xx は状態が変わらない限り何度押しても同じ結果になるので、ボタンを出さない
 * (押させておいて同じ失敗を繰り返させるのは、直せると誤解させるだけ)。
 * 5xx と通信断は一時的なことがあるので出す。
 */
export function isRetryable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true // 通信断
  return err.status >= 500
}
